import React, { useState, useEffect, useContext } from "react";
import { AdminDataContext } from "../../../useContext/AdminContext";
import moment from "moment";
import API from "../../../api";
import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import "../product/Product.css";

const WebhookManager = () => {
  const { snackbar, setIsLoading } = useContext(AdminDataContext);

  // State management
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingWebhookId, setUpdatingWebhookId] = useState(null);

  // Fetch webhooks
  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await API.get("/api/v1/webhooks/list");

      if (response.data.success) {
        setWebhooks(response.data.data || []);
      } else {
        snackbar("Failed to fetch webhooks", "error");
      }
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      snackbar("Error fetching webhooks", "error");
    } finally {
      setLoading(false);
    }
  };

  // Toggle webhook status
  const toggleWebhookStatus = async (webhookId, currentStatus) => {
    try {
      setUpdatingWebhookId(webhookId);
      const response = await API.put(`/api/v1/webhooks/${webhookId}/status`, {
        is_active: !currentStatus,
      });

      if (response.data.success) {
        snackbar(
          `Webhook ${
            !currentStatus ? "activated" : "deactivated"
          } successfully`,
          "success"
        );
        // Refresh webhooks list
        fetchWebhooks();
      } else {
        snackbar(
          response.data.message || "Failed to update webhook status",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating webhook status:", error);
      const errorMessage =
        error.response?.data?.message || "Error updating webhook status";
      snackbar(errorMessage, "error");
    } finally {
      setUpdatingWebhookId(null);
    }
  };

  // Initial load
  useEffect(() => {
    fetchWebhooks();
  }, []);

  // Event handlers
  const handleRefresh = () => {
    fetchWebhooks();
  };

  return (
    <div className="product-container">
      {/* Header */}
      <div className="product-header">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8} md={8}>
            <h1 className="bc-products-title">Webhook Management</h1>
            <p className="bc-products-subtitle">
              Monitor and manage BigCommerce webhooks for product and order
              events
            </p>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            md={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "start", sm: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
                boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                  boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </div>

      {/* Info Bar */}
      <div className="search-filter-bar">
        <div className="filter-row" style={{ gridTemplateColumns: "1fr" }}>
          <div style={{ fontSize: "0.875rem", color: "#666" }}>
            <strong>Active Webhooks:</strong>{" "}
            {webhooks.filter((w) => w.is_active).length} / {webhooks.length}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : webhooks.length === 0 ? (
          <div className="empty-state">
            <h3 className="empty-state-title">No webhooks found</h3>
            <p className="empty-state-text">
              No webhooks are configured at the moment
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Scope</th>
                  <th>Destination</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => {
                  const isUpdating = updatingWebhookId === webhook.id;
                  const createdDate = moment
                    .unix(webhook.created_at)
                    .format("MMM DD, YYYY");
                  const updatedDate = moment
                    .unix(webhook.updated_at)
                    .format("MMM DD, YYYY");

                  return (
                    <tr key={webhook.id}>
                      <td>
                        <span className="text-secondary">{webhook.id}</span>
                      </td>
                      <td>
                        <div
                          style={{
                            minWidth: "200px",
                            maxWidth: "300px",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          <span className="badge badge-info">
                            {webhook.scope}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div
                          className="text-secondary"
                          style={{
                            fontSize: "0.75rem",
                            minWidth: "250px",
                            maxWidth: "400px",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          {webhook.destination}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            webhook.is_active ? "badge-success" : "badge-error"
                          }`}
                        >
                          {webhook.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="date-text">{createdDate}</td>
                      <td className="date-text">{updatedDate}</td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn btn-small"
                            onClick={() =>
                              toggleWebhookStatus(webhook.id, webhook.is_active)
                            }
                            disabled={isUpdating}
                            style={{
                              minWidth: "80px",
                              background: webhook.is_active
                                ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
                                : "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                              color: "white",
                              border: "none",
                              boxShadow: webhook.is_active
                                ? "0 2px 4px rgba(244, 67, 54, 0.4)"
                                : "0 2px 4px rgba(76, 175, 80, 0.4)",
                            }}
                          >
                            {isUpdating ? (
                              <>
                                <span className="spinner spinner-small"></span>
                              </>
                            ) : (
                              <>
                                {webhook.is_active ? "Deactivate" : "Activate"}
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Footer */}
      {webhooks.length > 0 && (
        <div className="pagination-info">
          <div className="pagination-text">
            Total Webhooks: {webhooks.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookManager;
